import React, { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import ExternalLink from '@components/external-link/ExternalLink';
import LinkButton from '@components/link/linkButton';
import './markdownRenderer.scss';

const emailRegex = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const getComponentRenderers = (displayInline: boolean) => ({
  p: ({ children }: React.HTMLAttributes<HTMLParagraphElement>) =>
    displayInline ? <span>{children}</span> : <div className='markdown-renderer__paragraph'>{children}</div>,
  ul: ({ children }: React.HTMLAttributes<HTMLUListElement>) => <ul className='markdown-renderer__list'>{children}</ul>,
  ol: ({ children }: React.HTMLAttributes<HTMLOListElement>) => <ol className='markdown-renderer__list'>{children}</ol>,
  li: ({ children }: React.LiHTMLAttributes<HTMLLIElement>) => (
    <li className='markdown-renderer__list-item'>{children}</li>
  ),
  a: ({ href, children }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
    if (!href) return <>{children}</>;
    if (href.startsWith('email:') || href.match(emailRegex)) {
      const email = href.replace('email:', '');
      return (
        <LinkButton
          href={`mailto:${email}`}
          onClick={(e: { preventDefault: () => void }) => {
            e.preventDefault();
            window.location.href = `mailto:${email}`;
          }}
          title={email}
          content={email}
          className=''
          aria-label={`Send an email to ${email}`}
        >
           {children}
        </LinkButton>
      );
    }
    return <ExternalLink href={href} content={children} aria-label={children as string} />;
  },
});

export interface MarkdownRendererProps {
  text: string | ReactNode | null | undefined;
  displayInline?: boolean;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ text, displayInline = false }) => {
  if (text == null) return null;
  if (typeof text !== 'string') return <>{text}</>;

  const fixedMarkdown = text.replace(/\]\s+\(/g, '](');
  const normalizedText = fixedMarkdown.replace(emailRegex, (email) => {
    return `[email:${email}](${email})`;
  });

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={getComponentRenderers(displayInline)}>
       {normalizedText}
    </ReactMarkdown>
  );
};
